import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex, Modal, Text, Box, toast } from "dash-ui";
import {
  useApprovePropertyMutation,
  useAuditPropertyMutation,
  useArchivePropertyMutation,
  GetPropertiesDocument,
} from "gql/generated.graphql";
import {
  CmsActionButtonsProps,
  ActionType,
  ACTION_CONFIGS,
  DEFAULT_MESSAGES,
} from "./CmsActionButtons.constants";

export const CmsActionButtons = ({
  propertyId,
  currentStatus,
  onStatusChange,
}: CmsActionButtonsProps) => {
  const { t } = useTranslation("common", { keyPrefix: "labels" });
  const [openModal, setOpenModal] = useState<ActionType | null>(null);
  const [loadingAction, setLoadingAction] = useState<ActionType | null>(null);

  // Initialize mutations
  const [approveProperty] = useApprovePropertyMutation({
    refetchQueries: [GetPropertiesDocument],
  });
  const [auditProperty] = useAuditPropertyMutation({
    refetchQueries: [GetPropertiesDocument],
  });
  const [archiveProperty] = useArchivePropertyMutation({
    refetchQueries: [GetPropertiesDocument],
  });

  // Map actions to their mutations
  const mutations = useMemo(
    () => ({
      approve: approveProperty,
      audit: auditProperty,
      archive: archiveProperty,
    }),
    [approveProperty, auditProperty, archiveProperty]
  );

  // Determine which actions are available based on current status
  const availableActions = useMemo(() => {
    if (!currentStatus) return [];

    return (Object.keys(ACTION_CONFIGS) as ActionType[]).filter((action) =>
      ACTION_CONFIGS[action].canPerform(currentStatus)
    );
  }, [currentStatus]);

  // Generic action handler with error handling
  const handleAction = useCallback(
    async (actionType: ActionType) => {
      setLoadingAction(actionType);
      const config = ACTION_CONFIGS[actionType];

      try {
        const mutation = mutations[actionType];
        await mutation({
          variables: {
            input: { propertyId },
          },
        });

        // Show success toast
        const successMessage =
          t(config.successMessage) || DEFAULT_MESSAGES[config.successMessage];
        toast.success(successMessage);

        setOpenModal(null);
        onStatusChange?.();
      } catch (error: any) {
        const errorMessage =
          t(config.errorMessage) || DEFAULT_MESSAGES[config.errorMessage];
        const fullErrorMessage = error.message
          ? `${errorMessage}: ${error.message}`
          : errorMessage;
        toast.error(fullErrorMessage);

        console.error(`Failed to ${actionType} property:`, error);
      } finally {
        setLoadingAction(null);
      }
    },
    [propertyId, mutations, onStatusChange, t]
  );

  if (!currentStatus || availableActions.length === 0) return null;

  return (
    <>
      <Flex alignItems="center">
        {availableActions.map((action) => {
          const config = ACTION_CONFIGS[action];

          return (
            <Button
              key={action}
              ml={2}
              medium
              variation="primary"
              colorVariation={config.colorVariation}
              onClick={() => setOpenModal(action)}
              disabled={loadingAction !== null}
            >
              {t(config.buttonLabel) || DEFAULT_MESSAGES[config.buttonLabel]}
            </Button>
          );
        })}
      </Flex>

      {/* Reusable Confirmation Modal */}
      {openModal && (
        <Modal
          isOpen={true}
          onRequestClose={() => setOpenModal(null)}
          title={
            t(ACTION_CONFIGS[openModal].title) ||
            DEFAULT_MESSAGES[ACTION_CONFIGS[openModal].title]
          }
        >
          <Box mt={4}>
            <Box mb={8}>
              <Text>
                {t(ACTION_CONFIGS[openModal].message) ||
                  DEFAULT_MESSAGES[ACTION_CONFIGS[openModal].message]}
              </Text>
            </Box>
            <Flex justifyContent="flex-start">
              <Button
                variation="outline"
                mr={2}
                onClick={() => setOpenModal(null)}
                disabled={loadingAction !== null}
              >
                {t("cancel") || DEFAULT_MESSAGES.cancel}
              </Button>
              <Button
                variation="primary"
                colorVariation={ACTION_CONFIGS[openModal].colorVariation}
                onClick={() => handleAction(openModal)}
                disabled={loadingAction !== null}
              >
                {loadingAction === openModal
                  ? t(ACTION_CONFIGS[openModal].loadingLabel) ||
                    DEFAULT_MESSAGES[ACTION_CONFIGS[openModal].loadingLabel]
                  : t(ACTION_CONFIGS[openModal].buttonLabel) ||
                    DEFAULT_MESSAGES[ACTION_CONFIGS[openModal].buttonLabel]}
              </Button>
            </Flex>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default CmsActionButtons;
